/**
 * これまでの歩み — エージェントの日誌（Daily md）ビューア（2026-07-02 P3）。
 *
 * エージェントが日々のタスク完了・会話から S3 に書き溜めている Markdown 日誌を
 * 本人が読めるページ。従来は夕方の振り返りメールでしか見えず、「蓄積が見える」
 * 体験が欠けていた。参照は本人の日誌のみ（サーバ側で Cognito sub を強制）。
 */
import { useCallback, useEffect, useState } from "react"
import ReactMarkdown from "react-markdown"
import remarkGfm from "remark-gfm"
import { ChevronLeft, ChevronRight, Loader2 } from "lucide-react"
import { useAuth } from "@/hooks/useAuth"
import { Button } from "@/components/ui/button"
import { listDailyDates, getDaily } from "@/services/boardService"

function formatDateJa(dateStr: string): string {
  const d = new Date(`${dateStr}T00:00:00+09:00`)
  if (Number.isNaN(d.getTime())) return dateStr
  return d.toLocaleDateString("ja-JP", {
    year: "numeric",
    month: "long",
    day: "numeric",
    weekday: "short",
    timeZone: "Asia/Tokyo",
  })
}

export default function DailyJourney() {
  const { user } = useAuth()
  const idToken = user?.id_token || ""

  const [dates, setDates] = useState<string[]>([])
  const [selected, setSelected] = useState<string | null>(null)
  const [markdown, setMarkdown] = useState("")
  const [isLoadingList, setIsLoadingList] = useState(true)
  const [isLoadingBody, setIsLoadingBody] = useState(false)
  const [error, setError] = useState("")

  useEffect(() => {
    if (!idToken) return
    listDailyDates(idToken)
      .then((r) => {
        setDates(r.dates)
        if (r.dates.length > 0) setSelected(r.dates[0])
      })
      .catch((e) => setError(e instanceof Error ? e.message : String(e)))
      .finally(() => setIsLoadingList(false))
  }, [idToken])

  const loadBody = useCallback(
    async (date: string) => {
      if (!idToken) return
      setIsLoadingBody(true)
      setError("")
      try {
        const r = await getDaily(idToken, date)
        setMarkdown(r.markdown)
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e))
        setMarkdown("")
      } finally {
        setIsLoadingBody(false)
      }
    },
    [idToken]
  )

  useEffect(() => {
    if (selected) loadBody(selected)
  }, [selected, loadBody])

  // dates は新しい順。「前の日」=古い方（index+1）、「次の日」=新しい方（index-1）
  const idx = selected ? dates.indexOf(selected) : -1
  const older = idx >= 0 && idx < dates.length - 1 ? dates[idx + 1] : null
  const newer = idx > 0 ? dates[idx - 1] : null

  return (
    <div className="p-6 max-w-3xl mx-auto w-full">
      <h1 className="text-2xl font-bold mb-1">これまでの歩み</h1>
      <p className="text-sm text-muted-foreground mb-4">
        AIアシスタントが日々のタスクや会話から書き留めている日誌です。
      </p>

      {error && (
        <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-4">
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {isLoadingList ? (
        <div className="flex justify-center py-12">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </div>
      ) : dates.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground text-sm">
          まだ記録がありません。タスクが完了すると、その日の歩みがここに残っていきます。
        </div>
      ) : (
        <>
          <div className="flex items-center gap-2 mb-4 flex-wrap">
            <Button
              size="sm"
              variant="outline"
              disabled={!older}
              onClick={() => older && setSelected(older)}
              title="前の日へ"
            >
              <ChevronLeft className="h-4 w-4" />
              前の日
            </Button>
            <select
              value={selected ?? ""}
              onChange={(e) => setSelected(e.target.value)}
              className="border rounded-md px-2 py-1.5 text-sm bg-background"
              aria-label="日付を選択"
            >
              {dates.map((d) => (
                <option key={d} value={d}>
                  {formatDateJa(d)}
                </option>
              ))}
            </select>
            <Button
              size="sm"
              variant="outline"
              disabled={!newer}
              onClick={() => newer && setSelected(newer)}
              title="次の日へ"
            >
              次の日
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>

          <div className="border rounded-lg p-6 bg-card">
            {isLoadingBody ? (
              <div className="flex justify-center py-8">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              </div>
            ) : markdown ? (
              <div className="markdown-body">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>{markdown}</ReactMarkdown>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground text-center py-8">
                この日の記録はありません。
              </p>
            )}
          </div>
        </>
      )}
    </div>
  )
}
